import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { Compass, UsersThree, ChatsCircle, ArrowRight, GraduationCap } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { InviteCard } from "@/components/InviteCard";
import { InviteShareActions } from "@/components/InviteDialog";

export default function InvitePage() {
  const { kind, id } = useParams(); // kind: 'inst' | 'group'
  const type = kind === "inst" ? "institution" : "group";
  const [data, setData] = useState(null);
  const cardRef = useRef(null);
  useSeo(data ? `You're invited — ${data.name}` : "You're invited", "Explore, join and contribute on AlumniPulse.");
  useEffect(() => {
    api.get(`/public/invite/${type}/${id}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [type, id]);

  if (data === false) return <main className="mx-auto max-w-lg px-5 py-24 text-center text-muted-foreground">This invite is no longer available.</main>;

  const joinPath = type === "institution" ? (data?.primary_group_id ? `/groups/${data.primary_group_id}` : "/alumni-groups") : `/groups/${id}`;
  const showDiscussion = type === "group" || !!data?.primary_group_id;

  return (
    <main className="mx-auto grid max-w-5xl gap-10 px-5 py-14 lg:grid-cols-2 lg:items-center">
      <div className="flex justify-center">
        {!data ? <div className="h-80 w-[400px] animate-pulse rounded-3xl bg-muted/40" /> : <InviteCard ref={cardRef} data={data} />}
      </div>

      <div>
        <p className="overline text-secondary mb-2">You're invited to AlumniPulse</p>
        <h1 className="font-heading text-3xl font-light tracking-tight sm:text-4xl">{data?.name || "Loading…"}</h1>
        <p className="mt-3 text-muted-foreground">Reconnect with classmates, follow news &amp; events, and join the conversation — free for every alum.</p>

        <div className="mt-6 space-y-3">
          <Link to={data?.explore_path || "/institutions"}>
            <Button className="w-full justify-between rounded-2xl px-5 py-6 text-base" data-testid="invite-cta-explore"><span className="flex items-center gap-2.5"><Compass size={20} weight="duotone" /> Explore the page</span><ArrowRight size={18} /></Button>
          </Link>
          <Link to={joinPath}>
            <Button variant="outline" className="w-full justify-between rounded-2xl px-5 py-6 text-base" data-testid="invite-cta-join"><span className="flex items-center gap-2.5"><UsersThree size={20} weight="duotone" /> Join the alumni community</span><ArrowRight size={18} /></Button>
          </Link>
          {showDiscussion && (
            <Link to={joinPath}>
              <Button variant="outline" className="w-full justify-between rounded-2xl px-5 py-6 text-base" data-testid="invite-cta-discussion"><span className="flex items-center gap-2.5"><ChatsCircle size={20} weight="duotone" /> Join the discussion</span><ArrowRight size={18} /></Button>
            </Link>
          )}
        </div>

        <div className="mt-6 border-t border-border/60 pt-5">
          <p className="mb-2 text-sm font-medium text-muted-foreground">Pass it on</p>
          {data && <InviteShareActions type={type} id={id} cardRef={cardRef} name={data.name} />}
        </div>

        <Link to="/register" className="mt-6 flex items-center gap-1.5 text-sm text-secondary hover:underline" data-testid="invite-get-started"><GraduationCap size={16} /> New here? Create your free alumni profile</Link>
      </div>
    </main>
  );
}
